import { useRef } from "react";
import { Radar } from "react-chartjs-2";

import {
  CategoryScale,
  Chart,
  Filler,
  LinearScale,
  LineElement,
  Plugin,
  PointElement,
  RadialLinearScale
} from "chart.js";

import { SessionExtra } from "../../store/Session/SessionModel";
import { formatDrillApiTargetQuality } from "../../store/Session/helper/combineTargetQualityDisplay";
import { formatDrillTargetName } from "../../store/Session/helper/UnitConversions";

import _ from "lodash";
import { shotQualityRanges } from "../../config/CombineTutorial";
import {
  createGradient,
  generatePointValue,
  generateTicks
} from "./CombineChartOptions";

Chart.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  RadialLinearScale,
  Filler
);

interface Props {
  sessionData: SessionExtra;
}

const CombineSummaryChart = ({ sessionData }: Props): JSX.Element => {
  const chartRef = useRef<Chart<"radar"> | null>(null);
  const { drillTargets }: any = sessionData;
  if (!drillTargets) {
    return <></>;
  }
  let labels = [] as string[];
  let dataChart = [] as number[];
  let dataSets = [{ value: null, label: "" }] as any[];

  // KWSA-34: the axes must run in ascending target distance, matching the Golf App.
  // This used to sort ascending and then `push(shift())`, which rotated the whole list
  // — a 60/70/80 Combine rendered its axes as 70, 80, 60 and every target's score sat
  // on the wrong spoke. The rotation only did the right thing for a Tee target, whose
  // distance is null and which therefore sorted to the front; sorting Tee explicitly
  // last keeps that while leaving the real distances in order.
  const targetSortKey = (target: any) =>
    target?.distance?.maxDistance || target?.distance?.distance == null
      ? Infinity
      : target.distance.distance;
  let newDrillTargets = [...drillTargets];
  newDrillTargets.sort((a, b) => targetSortKey(a) - targetSortKey(b));

  newDrillTargets?.map((target: any) => {
    dataSets.push({
      value: formatDrillApiTargetQuality(target?.targetQuality) ?? 0,
      label: target?.distance?.distance ?? 0
    });
    return target;
  });
  const maxTargetQuality = _.maxBy(newDrillTargets, "targetQuality");
  let suggestedMax = 100;
  if (maxTargetQuality) {
    const targetQuality = maxTargetQuality?.targetQuality;
    if (targetQuality > 100 && targetQuality <= 150) {
      suggestedMax = 150;
    } else if (targetQuality > 150 && targetQuality <= 200) {
      suggestedMax = 200;
    } else {
      suggestedMax = Math.ceil(targetQuality / 50) * 50;
    }
  }
  labels = dataSets.map((data) =>
    data.label === "" ? "" : formatDrillTargetName(data.label)
  );
  dataChart = dataSets.map((data) => data.value);

  const data = {
    labels,
    datasets: [
      {
        label: "",
        data: dataChart,
        pointBackgroundColor: "#7F66FF",
        pointBorderColor: "white",
        backgroundColor: (ctx: any) => {
          return createGradient(ctx.chart.ctx, ctx.chart.chartArea);
        },
        borderColor: "#AF40FF50",
        borderWidth: 0.5,
        fill: true,
        spanGaps: true
      }
    ]
  };

  const options = {
    responsive: true,
    layout: {
      autoPadding: true
    },
    scales: {
      r: {
        suggestedMin: 0,
        suggestedMax,
        angleLines: {
          color: (context: any) => {
            return context.index === 0 ? "transparent" : "#494A4C";
          }
        },
        grid: {
          circular: true,
          color: (context: any) => {
            return context.index === 2 ? "#76777A" : "#494A4C";
          }
        },
        pointLabels: {
          display: true,
          font: () => {
            return {
              size: 14,
              weight: "400",
              family: "Davis Sans-Medium, sans-serif"
            };
          },
          color: "#ffffff"
        },
        ticks: {
          display: false,
          backdropColor: "#00000080",
          backdropPadding: 5,
          padding: 5,
          font: {
            size: 10,
            weight: "700",
            family: "Davis Sans-Medium, sans-serif"
          },
          color: "#ffffff",
          stepSize: 50,
          z: 10
        }
      }
    }
  };
  const quality = sessionData?.sessionQuality ?? 0;
  const shotQuality = shotQualityRanges(quality);

  const shotImage = new Image();
  shotImage.src = `/img/combine/fs_combine_score.png`;
  shotImage.crossOrigin = "anonymous";

  let imageLoaded = false;
  shotImage.onload = () => {
    imageLoaded = true;
  };
  const generateShotQuality = (chart: any) => {
    const ctx = chart.ctx;
    const scale = chart.scales.r;
    ctx.save();

    if (imageLoaded) {
      // ctx.drawImage(shotImage, scale.xCenter - 21, scale.yCenter - 23, 42, 49);
      ctx.drawImage(shotImage, scale.xCenter - 31, scale.yCenter - 33, 62, 69);
    } else {
      shotImage.onload = () => {
        imageLoaded = true;
        chart.draw();
      };
    }

    // Draw session quality
    ctx.font = "bold 20px Stratos-MediumItalic";
    ctx.fillStyle = "white";
    ctx.textAlign = "center";
    ctx.fillText(quality?.toFixed() ?? 0, scale.xCenter, scale.yCenter + 8);

    // Draw quality text
    ctx.font = "bold 16px Davis Sans-bold";
    ctx.fillStyle = "white";
    ctx.textAlign = "center";
    ctx.fillText(shotQuality.txtRange, scale.xCenter, scale.yCenter + 55);

    ctx.restore();
  };
  let plugins: Plugin[] = [
    {
      id: "TICKS",
      afterDraw: generateTicks
    },
    {
      id: "POINT_VALUE",
      afterDatasetsDraw: generatePointValue
    },
    {
      id: "SHOT_QUALITY",
      afterDraw: generateShotQuality
    }
  ];

  return (
    <div className="chart-wrapper">
      <Radar data={data} options={options} plugins={plugins} ref={chartRef} />
    </div>
  );
};

export default CombineSummaryChart;
